import { type ReactNode, useId, useState } from 'react';

import { BankSelect, Button, Dialog, DialogFooter, inputCls } from './ui';

/** 입금자명 비교 — 띄어쓰기·대소문자 차이는 같은 이름 (서버와 같은 기준) */
export function sameDepositor(a: string, b: string): boolean {
  const normalize = (v: string) => v.replace(/\s+/g, '').toLowerCase();
  return normalize(a) === normalize(b);
}

export interface BankAccountInput {
  bank: string;
  accountNumber: string;
  holder: string;
}

export const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;

/** 사유(5자 이상) + 반환 계좌 — 입금 반환이 생기는 모든 처리에 공통 */
export function ReturnFields({
  reason,
  onReason,
  bank,
  onBank,
  reasonLabel = '사유 (5자 이상, 구매자에게도 보여요)',
  reasonPlaceholder,
  withAccount = true,
  accountLocked = false,
}: {
  reason: string;
  onReason(v: string): void;
  bank: BankAccountInput;
  onBank(v: BankAccountInput): void;
  reasonLabel?: string;
  reasonPlaceholder: string;
  withAccount?: boolean;
  /**
   * 구매자가 등록한 환불 계좌로 돌려줄 때 — 계좌를 고칠 수 없게 보여 주기만 한다 (서버도 그 계좌로 만든다).
   * 다른 계좌로 보내야 하면 만든 뒤 입금 반환의 [계좌 수정]으로
   */
  accountLocked?: boolean;
}) {
  const id = useId();
  return (
    <div className="space-y-2.5">
      <label htmlFor={id} className="block">
        <span className="text-xs font-semibold text-muted-foreground">{reasonLabel}</span>
        <textarea
          id={id}
          rows={2}
          maxLength={500}
          className={`mt-1 w-full resize-none py-2 ${inputCls}`}
          placeholder={reasonPlaceholder}
          value={reason}
          onChange={(e) => onReason(e.target.value)}
        />
      </label>
      {withAccount && accountLocked && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground">
            돌려줄 계좌 · 구매자가 등록한 환불 계좌
          </p>
          <p className="mt-1 rounded-md bg-muted px-3 py-2 text-sm font-semibold">
            {bank.bank} {bank.accountNumber}
            <span className="ml-1.5 font-normal text-muted-foreground">({bank.holder})</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            다른 계좌로 보내야 하면 처리한 뒤 입금 반환에서 [계좌 수정]으로 바꿔요.
          </p>
        </div>
      )}
      {withAccount && !accountLocked && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground">돌려줄 계좌</p>
          <div className="mt-1 grid grid-cols-[minmax(0,1fr)_7rem] gap-1.5">
            <BankSelect
              className="w-full min-w-0"
              value={bank.bank}
              onChange={(v) => onBank({ ...bank, bank: v })}
            />
            <input
              className={`w-full min-w-0 ${inputCls}`}
              placeholder="예금주"
              value={bank.holder}
              onChange={(e) => onBank({ ...bank, holder: e.target.value })}
            />
            <input
              className={`col-span-2 w-full min-w-0 ${inputCls}`}
              placeholder="계좌번호"
              inputMode="numeric"
              value={bank.accountNumber}
              onChange={(e) => onBank({ ...bank, accountNumber: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export const EMPTY_BANK: BankAccountInput = { bank: '', accountNumber: '', holder: '' };

/** 구매자가 등록한 환불 계좌 → 계좌 입력값. 없으면 빈 칸 */
export function refundBankOf(
  account: { bankName: string; accountNumber: string; holderName: string } | null | undefined,
): BankAccountInput {
  return account
    ? { bank: account.bankName, accountNumber: account.accountNumber, holder: account.holderName }
    : EMPTY_BANK;
}

/** 미리 채운 계좌 안내 문구 */
export const REFUND_ACCOUNT_NOTE = '구매자가 등록한 환불 계좌로 채웠어요';

export function bankOk(b: BankAccountInput): boolean {
  return !!b.bank.trim() && b.accountNumber.trim().length >= 4 && !!b.holder.trim();
}

export function trimBank(b: BankAccountInput): BankAccountInput {
  return { bank: b.bank.trim(), accountNumber: b.accountNumber.trim(), holder: b.holder.trim() };
}

/**
 * 무통장 입금 확인 — 어드민이 은행 거래 내역을 보고 실제 입금액과 입금자명을 직접 옮겨 적는다.
 * 입금자명이 다르면 기본은 확인할 수 없고, 구매자가 본인 입금을 입증한 경우에만 근거를 적고 처리한다.
 * 금액이 다르면:
 *  - 많으면 결제 금액만큼 확인하고 넘친 금액은 입금 반환 (사유·계좌 필수)
 *  - 적으면 확인하지 않는다 — 받은 금액을 돌려주고(주문 취소) 다시 주문하도록 (onUnderpayment 가 있을 때)
 */
export function ConfirmDepositDialog({
  title,
  expectedAmount,
  expectedDepositor,
  note,
  underpaymentNote = '',
  allowOverpayment = false,
  busy,
  error,
  onSubmit,
  onUnderpayment,
  onClose,
  children,
  defaultBankAccount,
}: {
  title: string;
  /** 받아야 할 금액 (묶음이면 묶음 합계) */
  expectedAmount: number;
  /** 구매자에게 안내한 입금자명 */
  expectedDepositor: string | null;
  /** 확인하면 일어나는 일 안내 */
  note: string;
  /** 부족 입금 반환을 하면 일어나는 일 (주문 취소 등) */
  underpaymentNote?: string;
  /** 안내 아래 덧붙일 내용 (함께 확인되는 묶음 주문 등) */
  children?: ReactNode;
  /** 반환 계좌 칸을 미리 채울 계좌 — 구매자가 등록한 환불 계좌 */
  defaultBankAccount?: BankAccountInput | null;
  /** 초과 입금을 결제 금액만큼 확인하고 넘친 금액을 반환할 수 있는지 (주문 입금 확인만) */
  allowOverpayment?: boolean;
  busy: boolean;
  /** 서버가 거절한 이유 (금액·입금자명 불일치 등) — 창을 닫지 않고 보여 준다 */
  error?: string | null;
  onSubmit(input: {
    receivedAmount: number;
    depositorName: string;
    overrideReason?: string;
    overpayment?: { reason: string; bankAccount: BankAccountInput };
  }): void;
  /** 부족 입금 반환 — 없으면 금액이 적을 때 확인만 막는다 (로열티 입금 등) */
  onUnderpayment?(input: {
    receivedAmount: number;
    depositorName: string;
    reason: string;
    bankAccount: BankAccountInput;
  }): void;
  onClose(): void;
}) {
  const ids = { amount: useId(), name: useId(), reason: useId() };
  const [amountText, setAmountText] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [bank, setBank] = useState<BankAccountInput>(defaultBankAccount ?? EMPTY_BANK);
  const accountLocked = Boolean(defaultBankAccount);

  const amount = Number(amountText.replace(/[^\d]/g, ''));
  const amountEntered = amountText.trim() !== '' && amount > 0;
  // 초과·부족 처리를 쓰지 않는 화면은 금액이 다르면 확인만 막는다
  const mismatch = amountEntered && amount !== expectedAmount;
  const over = allowOverpayment && amountEntered && amount > expectedAmount;
  const under = !!onUnderpayment && amountEntered && amount < expectedAmount;
  const blockedMismatch = mismatch && !over && !under;
  const nameMismatch =
    !!name.trim() && !!expectedDepositor && !sameDepositor(name, expectedDepositor);
  const reasonOk = reason.trim().length >= 5;
  const returnOk = returnReason.trim().length >= 5 && bankOk(bank);

  const canConfirm =
    !busy &&
    amountEntered &&
    !under &&
    !blockedMismatch &&
    !!name.trim() &&
    (!nameMismatch || reasonOk) &&
    (!over || returnOk);
  const canReturn = !busy && under && !!name.trim() && returnOk;

  return (
    <Dialog label={title} title={title} onClose={onClose} wide>
      <p className="text-sm text-muted-foreground">
        은행 거래 내역에 찍힌 금액과 입금자명을 그대로 입력하세요. {note}
      </p>
      {children}

      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md bg-muted px-3.5 py-3 text-sm">
        <dt className="text-muted-foreground">받을 금액</dt>
        <dd className="text-right font-semibold tabular-nums">{won(expectedAmount)}</dd>
        <dt className="text-muted-foreground">안내한 입금자명</dt>
        <dd className="text-right font-semibold">{expectedDepositor ?? '—'}</dd>
      </dl>

      <div className="mt-4 space-y-3">
        <label htmlFor={ids.amount} className="block">
          <span className="text-xs font-semibold text-muted-foreground">실제 입금액 (원)</span>
          <input
            id={ids.amount}
            inputMode="numeric"
            className={`mt-1 w-full tabular-nums ${inputCls}`}
            placeholder={expectedAmount.toLocaleString('ko-KR')}
            value={amountText ? Number(amountText).toLocaleString('ko-KR') : ''}
            onChange={(e) => setAmountText(e.target.value.replace(/[^\d]/g, ''))}
            autoFocus
          />
          {blockedMismatch && (
            <span className="mt-1 block text-xs font-semibold text-rose-600">
              받을 금액과 {won(Math.abs(amount - expectedAmount))}{' '}
              {amount < expectedAmount ? '부족해요' : '많아요'}. 금액이 다르면 확인할 수 없어요.
            </span>
          )}
        </label>

        <label htmlFor={ids.name} className="block">
          <span className="text-xs font-semibold text-muted-foreground">은행 입금자명</span>
          <input
            id={ids.name}
            className={`mt-1 w-full ${inputCls}`}
            placeholder={expectedDepositor ?? '입금자명'}
            maxLength={64}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {nameMismatch && !under && (
            <span className="mt-1 block text-xs font-semibold text-rose-600">
              안내한 입금자명과 달라요. 구매자 본인 입금이 확인된 경우에만 아래에 근거를 적고
              처리하세요.
            </span>
          )}
        </label>

        {nameMismatch && !under && (
          <label htmlFor={ids.reason} className="block">
            <span className="text-xs font-semibold text-muted-foreground">
              본인 입금 확인 근거 (5자 이상, 기록에 남아요)
            </span>
            <textarea
              id={ids.reason}
              rows={2}
              className={`mt-1 w-full resize-none py-2 ${inputCls}`}
              placeholder="예: 구매자가 보낸 이체확인증의 계좌·시각·금액이 은행 내역과 일치"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        )}

        {over && (
          <div className="space-y-2.5 rounded-md bg-amber-50 px-3.5 py-3">
            <p className="text-sm font-semibold text-amber-800">
              {won(amount - expectedAmount)} 많이 들어왔어요
            </p>
            <p className="text-xs leading-relaxed text-amber-800">
              {won(expectedAmount)}으로 결제를 확인하고, 넘친 {won(amount - expectedAmount)}은 입금
              반환으로 기록해요. 송금한 뒤 주문 상세의 입금 반환에서 [반환 완료]를 눌러 주세요.
            </p>
            <ReturnFields
              reason={returnReason}
              onReason={setReturnReason}
              bank={bank}
              onBank={setBank}
              accountLocked={accountLocked}
              reasonPlaceholder="예: 구매자가 금액을 잘못 입력해 1만 원 더 보냄"
            />
          </div>
        )}

        {under && (
          <div className="space-y-2.5 rounded-md bg-red-50 px-3.5 py-3">
            <p className="text-sm font-semibold text-red-700">
              {won(expectedAmount - amount)} 적게 들어왔어요
            </p>
            <p className="text-xs leading-relaxed text-red-700">
              부족 입금은 확인하지 않아요. 받은 {won(amount)}을 돌려주고 다시 주문하도록 안내해
              주세요. {underpaymentNote}
            </p>
            <ReturnFields
              reason={returnReason}
              onReason={setReturnReason}
              bank={bank}
              onBank={setBank}
              accountLocked={accountLocked}
              reasonPlaceholder="예: 9만 원만 입금, 구매자와 통화 후 반환하고 재주문 안내"
            />
          </div>
        )}
      </div>

      <DialogFooter
        notice={
          error && (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )
        }
      >
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        {under ? (
          <Button
            variant="danger"
            className="flex-1"
            disabled={!canReturn}
            onClick={() =>
              onUnderpayment?.({
                receivedAmount: amount,
                depositorName: name.trim(),
                reason: returnReason.trim(),
                bankAccount: trimBank(bank),
              })
            }
          >
            받은 금액 반환
          </Button>
        ) : (
          <Button
            className="flex-1"
            disabled={!canConfirm}
            onClick={() =>
              onSubmit({
                receivedAmount: amount,
                depositorName: name.trim(),
                ...(nameMismatch ? { overrideReason: reason.trim() } : {}),
                ...(over
                  ? { overpayment: { reason: returnReason.trim(), bankAccount: trimBank(bank) } }
                  : {}),
              })
            }
          >
            {over ? '초과분 반환하고 확인' : nameMismatch ? '근거를 남기고 확인' : '입금 확인'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}
